<template>
	<a-modal
		class="g-pay-video-list-edit-modal"
		:visible="visible"
		title="多视频付费编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
			<PayMediaEditForm
				:form="form"
				mode="count"
				:max-count="form.items.length"
				:show-title="true"
				:show-subtitle="true"
				title-placeholder="例如 精选视频合集"
				subtitle-placeholder="例如 共 4 个精选视频"
			/>

			<div class="g-pay-video-list-edit">
				<div class="g-pay-video-list-edit__toolbar">
					<span v-if="form.items.length" class="g-pay-video-list-edit__hint">视频支持拖拽排序</span>
					<div class="g-pay-video-list-edit__toolbar-main">
						<span class="g-pay-video-list-edit__count">已添加 {{ form.items.length }} 个视频</span>
						<button
							type="button"
							class="g-pay-video-list-edit__upload-btn"
							:disabled="isUploading"
							@click="onAddVideos"
						>继续上传</button>
					</div>
				</div>

				<EditListSortGroup ref="sortList" class="g-pay-video-list-edit__grid" @dragover="onSortDragOver">
					<div
						v-for="(item, index) in form.items"
						:key="item.id"
						:data-sort-id="item.id"
						class="g-pay-video-list-edit__cell"
						:class="getEditListSortItemClass(dragId, item.id, dragOverId)"
						:draggable="!isActiveUploadItem(index) && !item.uploading"
						@dragstart="onDragStart(item.id)"
						@dragend="onDragEnd"
					>
						<div class="g-pay-video-list-edit__thumb">
							<UploadProgressPanel
								v-if="isActiveUploadItem(index)"
								:tasks="uploadTasks"
								variant="item"
								hide-filename
							/>
							<template v-else>
								<img v-if="getCoverUrl(item)" :src="getCoverUrl(item)" alt="" />
								<i v-else class="mbm-iconfont mbm-shipin"></i>
								<button
									type="button"
									class="g-pay-video-list-edit__play"
									aria-label="播放"
									@click.stop="playEditMedia(getVideoUrl(item), 'video')"
								>
									<span class="g-pay-video-list-edit__triangle"></span>
								</button>
								<span class="g-pay-video-list-edit__duration">{{ item.video?.durationText || '00:00' }}</span>
							</template>
						</div>
						<input
							v-model="item.title"
							type="text"
							class="g-pay-video-list-edit__title-input"
							maxlength="40"
							placeholder="标题"
						/>
						<input
							v-model="item.description"
							type="text"
							class="g-pay-video-list-edit__desc-input"
							:style="{ color: item.descColor }"
							maxlength="40"
							placeholder="描述"
						/>
						<div class="g-pay-video-list-edit__actions">
							<button type="button" class="g-pay-video-list-edit__action-btn" @click="onReplaceVideo(index)">更换</button>
							<button type="button" class="g-pay-video-list-edit__action-btn" @click="onReplaceCover(index)">换封面</button>
							<button
								type="button"
								class="g-pay-video-list-edit__action-btn is-delete"
								:disabled="form.items.length <= 1"
								@click="onDeleteItem(index)"
							>删除</button>
						</div>
					</div>
					<div
						v-if="isUploading && uploadMode === 'append'"
						class="g-pay-video-list-edit__cell g-pay-video-list-edit__cell--append-upload"
					>
						<div class="g-pay-video-list-edit__thumb">
							<UploadProgressPanel
								:tasks="uploadTasks"
								:merge="uploadTasks.length > 1"
								variant="item"
								hide-filename
							/>
						</div>
					</div>
					<div v-if="!form.items.length && !isUploading" class="g-pay-video-list-edit__empty">
						请上传视频文件
					</div>
				</EditListSortGroup>
			</div>
		</div>

		<input
			ref="coverInput"
			type="file"
			accept="image/*"
			class="g-pay-video-list-edit__file-input"
			@change="onCoverSelect"
		/>
		<input
			ref="videoInput"
			type="file"
			accept="video/*,.mp4,.mov,.avi,.flv,.mkv,.webm,.m4v,.wmv"
			class="g-pay-video-list-edit__file-input"
			multiple
			@change="onVideoSelect"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" :disabled="!form.items.length || isUploading" @click="onConfirm">确认</a-button>
		</template>

		<EditMediaPlayerOverlay
			:url="editPlayingUrl"
			:kind="editPlayingKind"
			@close="stopEditMediaPlay"
		/>
	</a-modal>
</template>

<script>
	import PayMediaEditForm from './PayMediaEditForm.vue';
	import EditMediaPlayerOverlay from './EditMediaPlayerOverlay.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import editModalMediaPlay from '../mixins/editModalMediaPlay.js';
	import {
		isLocalImageFile,
		MAX_LOCAL_IMAGE_SIZE,
	} from '../utils/localImage.js';
	import { filterValidLocalVideoList } from '../utils/localVideo.js';
	import {
		createDefaultVideoListItem,
		getVideoListCoverUrl,
		getVideoListVideoUrl,
		replaceVideoListCover,
		replaceVideoListVideo,
		revokeVideoListItems,
	} from '../utils/videoList.js';
	import {
		getPayVideoListDefaults,
		normalizePayVideoListData,
	} from '../utils/payVideoList.js';
	import EditListSortGroup from './EditListSortGroup.vue';
	import {
		getEditListSortItemClass,
		handleEditListSortDragEnd,
		handleEditListSortDragOver,
		handleEditListSortDragStart,
	} from '../utils/editListDragSort.js';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GPayVideoListEditModal',
		components: { EditListSortGroup, EditMediaPlayerOverlay, PayMediaEditForm, UploadProgressPanel },
		mixins: [editModalMediaPlay],
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => getPayVideoListDefaults(),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: getPayVideoListDefaults(),
				isUploading: false,
				uploadTasks: [],
				uploadMode: 'append',
				replaceIndex: -1,
				coverReplaceIndex: -1,
				replaceUploadIndex: -1,
				dragId: '',
				dragOverId: '',
			};
		},
		watch: {
			visible(value) {
				if (value) {
					this.initForm();
				}
			},
		},
		beforeUnmount() {
			revokeVideoListItems(this.form.items);
		},
		methods: {
			getEditListSortItemClass,
			initForm() {
				const source = normalizePayVideoListData(this.data);
				this.form = {
					...source,
					items: source.items.map(item => ({ ...item })),
				};
			},
			getCoverUrl(item) {
				return getVideoListCoverUrl(item);
			},
			getVideoUrl(item) {
				return getVideoListVideoUrl(item);
			},
			createListItemFromVideo(video, index) {
				const item = createDefaultVideoListItem({}, index);
				const title = video.filename?.replace(/\.[^.]+$/, '') || item.title;
				return {
					...item,
					title,
					video: replaceVideoListVideo(item.video, video),
					cover: { id: '', filename: '', previewUrl: '', url: '', local: false },
				};
			},
			isActiveUploadItem(index) {
				return this.isUploading
					&& this.uploadMode === 'replace'
					&& index === this.replaceUploadIndex;
			},
			clearUploadState() {
				this.isUploading = false;
				this.uploadTasks = [];
				this.uploadMode = 'append';
				this.replaceIndex = -1;
				this.replaceUploadIndex = -1;
			},
			async uploadVideos(files, { mode = 'append', replaceIndex = -1 } = {}) {
				this.isUploading = true;
				this.uploadMode = mode;
				this.uploadTasks = createUploadTasks(files);

				if (mode === 'replace') {
					this.replaceUploadIndex = replaceIndex;
					this.form.items[replaceIndex].uploading = true;
				}

				try {
					const items = await uploadHeaderMediaFiles(this, {
						type: 'video',
						files,
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					if (mode === 'append') {
						const startIndex = this.form.items.length;
						this.form.items.push(
							...items.map((video, index) => this.createListItemFromVideo(video, startIndex + index)),
						);
					} else if (mode === 'replace') {
						const old = this.form.items[replaceIndex];
						const next = this.createListItemFromVideo(items[0], replaceIndex);
						next.id = old.id;
						next.title = old.title || next.title;
						revokeVideoListItems([old]);
						this.form.items.splice(replaceIndex, 1, next);
					}
				} catch (error) {
					if (mode === 'replace' && this.form.items[replaceIndex]) {
						this.form.items[replaceIndex].uploading = false;
					}
					this.$message?.error?.('上传失败');
				} finally {
					this.clearUploadState();
				}
			},
			onAddVideos() {
				this.replaceIndex = -1;
				this.$refs.videoInput.value = '';
				this.$refs.videoInput.click();
			},
			onReplaceVideo(index) {
				this.replaceIndex = index;
				this.$refs.videoInput.value = '';
				this.$refs.videoInput.click();
			},
			onReplaceCover(index) {
				this.coverReplaceIndex = index;
				this.$refs.coverInput.value = '';
				this.$refs.coverInput.click();
			},
			onDeleteItem(index) {
				if (this.form.items.length <= 1) return;
				const [removed] = this.form.items.splice(index, 1);
				revokeVideoListItems([removed]);
			},
			onDragStart(id) {
				handleEditListSortDragStart(this, id);
			},
			onSortDragOver(event) {
				handleEditListSortDragOver(event, this.$refs.sortList, this);
			},
			onDragEnd() {
				handleEditListSortDragEnd(this, this.$refs.sortList, this.form.items, this);
			},
			async onVideoSelect(event) {
				const files = Array.from(event.target.files || []);
				event.target.value = '';
				if (!files.length) return;
				const validFiles = filterValidLocalVideoList(files, files.length);
				if (!validFiles.length) return;
				if (this.replaceIndex >= 0) {
					const replaceIndex = this.replaceIndex;
					this.replaceIndex = -1;
					await this.uploadVideos(validFiles.slice(0, 1), { mode: 'replace', replaceIndex });
					return;
				}
				await this.uploadVideos(validFiles, { mode: 'append' });
			},
			async onCoverSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !isLocalImageFile(file) || this.coverReplaceIndex < 0) return;
				if (file.size > MAX_LOCAL_IMAGE_SIZE) return;
				const item = this.form.items[this.coverReplaceIndex];
				if (!item) return;
				const coverReplaceIndex = this.coverReplaceIndex;
				this.coverReplaceIndex = -1;
				this.isUploading = true;
				this.uploadMode = 'replace';
				this.replaceUploadIndex = coverReplaceIndex;
				this.uploadTasks = createUploadTasks([file]);
				try {
					const [coverItem] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					item.cover = replaceVideoListCover(item.cover, file, () => coverItem);
				} catch (error) {
					this.$message?.error?.('上传失败');
					this.coverReplaceIndex = coverReplaceIndex;
				} finally {
					this.clearUploadState();
				}
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				this.$emit('confirm', normalizePayVideoListData({ ...this.form }));
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.g-pay-video-list-edit {
		margin-top: 8px;
		padding-top: 8px;
	}

	.g-pay-video-list-edit__toolbar {
		.edit-list-sort-toolbar-row();
		margin-bottom: 12px;
	}

	.g-pay-video-list-edit__toolbar-main {
		.edit-list-sort-toolbar-main();
	}

	.g-pay-video-list-edit__count {
		font-size: 13px;
		color: #666;
	}

	.g-pay-video-list-edit__upload-btn {
		padding: 0;
		border: none;
		background: transparent;
		font-size: 13px;
		color: #285af9;
		cursor: pointer;
		outline: none;

		&:disabled {
			color: #bbb;
			cursor: not-allowed;
		}
	}

	.g-pay-video-list-edit__grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 12px;
	}

	.g-pay-video-list-edit__cell {
		border: 1px solid #f0f0f0;
		border-radius: 10px;
		padding: 10px;
		background: #fff;
		box-sizing: border-box;
		cursor: grab;

		&:active {
			cursor: grabbing;
		}

		&--append-upload {
			cursor: default;
		}
	}

	.g-pay-video-list-edit__thumb {
		position: relative;
		width: 100%;
		aspect-ratio: 16 / 9;
		overflow: hidden;
		border-radius: 8px;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		i {
			font-size: 24px;
			color: #bfbfbf;
		}
	}

	.g-pay-video-list-edit__play {
		position: absolute;
		top: 50%;
		left: 50%;
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		margin: -18px 0 0 -18px;
		padding: 0;
		border: none;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.45);
		cursor: pointer;
	}

	.g-pay-video-list-edit__triangle {
		display: block;
		width: 0;
		height: 0;
		margin-left: 3px;
		border-top: 6px solid transparent;
		border-bottom: 6px solid transparent;
		border-left: 10px solid #fff;
	}

	.g-pay-video-list-edit__duration {
		position: absolute;
		left: 8px;
		bottom: 8px;
		z-index: 2;
		padding: 2px 6px;
		border-radius: 4px;
		background: rgba(0, 0, 0, 0.55);
		color: #fff;
		font-size: 11px;
		line-height: 1.4;
		pointer-events: none;
	}

	.g-pay-video-list-edit__title-input,
	.g-pay-video-list-edit__desc-input {
		display: block;
		width: 100%;
		padding: 0;
		border: none;
		background: transparent;
		outline: none;
		box-sizing: border-box;

		&::placeholder {
			color: #bbb;
			font-weight: normal;
		}
	}

	.g-pay-video-list-edit__title-input {
		margin-top: 8px;
		font-size: 14px;
		font-weight: 600;
		color: #333;
		line-height: 1.4;
	}

	.g-pay-video-list-edit__desc-input {
		margin-top: 4px;
		font-size: 12px;
		line-height: 1.5;
	}

	.g-pay-video-list-edit__actions {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		margin-top: 8px;
	}

	.g-pay-video-list-edit__action-btn {
		padding: 0;
		border: none;
		background: transparent;
		font-size: 12px;
		color: #285af9;
		cursor: pointer;
		outline: none;

		&:disabled {
			color: #ddd;
			cursor: not-allowed;
		}
	}

	.g-pay-video-list-edit__empty {
		grid-column: 1 / -1;
		padding: 28px 12px;
		text-align: center;
		font-size: 13px;
		color: #999;
		background: #fafafa;
		border-radius: 8px;
	}

	.g-pay-video-list-edit__hint {
		.edit-list-sort-hint-text();
	}

	.g-pay-video-list-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-pay-video-list-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();
	}
</style>
